import { useState } from "react";
import { useAuth } from "@/lib/AuthContext";
import { ChevronsUpDown, Plus, Check, Copy, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogClose } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export default function GuildSwitcher() {
  const { guilds, activeGuild, switchGuild, createGuild, createInvite, isAdmin } = useAuth();
  const [open, setOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);
  const [inviteRole, setInviteRole] = useState("officer");
  const [inviteCode, setInviteCode] = useState("");
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteBusy, setInviteBusy] = useState(false);

  if (!activeGuild) return null;

  const handleCreate = async () => {
    if (newName.trim().length < 3) { toast.error("Name must be 3+ chars"); return; }
    setBusy(true);
    try {
      await createGuild(newName.trim());
      toast.success("Guild created");
      window.location.reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
    setBusy(false);
  };

  const handleInvite = async () => {
    setInviteBusy(true);
    try {
      const res = await createInvite(activeGuild.id, inviteRole);
      setInviteCode(res.invite_code);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
    setInviteBusy(false);
  };

  const copyInvite = () => {
    navigator.clipboard.writeText(inviteCode);
    toast.success("Invite copied to clipboard");
  };

  return (
    <div className="border border-border/40 rounded-sm bg-card/30" data-testid="guild-switcher">
      <button
        data-testid="guild-switcher-trigger"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 px-3 py-2 hover:bg-card/60 transition-colors"
      >
        {/* Avatar */}
        <div className="w-8 h-8 rounded-sm bg-card/60 border border-border/40 overflow-hidden flex-shrink-0">
          {activeGuild.avatar_url ? (
            <img src={activeGuild.avatar_url} alt="" className="w-full h-full object-cover object-center" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-[10px] font-bold text-purple-400">
              {activeGuild.name?.[0]?.toUpperCase() || "G"}
            </div>
          )}
        </div>
        <div className="text-left overflow-hidden flex-1">
          <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Guild</div>
          <div className="text-sm font-bold truncate">{activeGuild.name}</div>
        </div>
        <ChevronsUpDown className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
      </button>

      {open && (
        <div className="border-t border-border/40 px-2 py-2 space-y-1" data-testid="guild-switcher-menu">
          {guilds.map((g) => (
            <button
              key={g.id}
              data-testid={`guild-option-${g.slug}`}
              onClick={async () => {
                if (g.id === activeGuild.id) { setOpen(false); return; }
                await switchGuild(g.id);
              }}
              className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-sm text-xs transition-colors ${
                g.id === activeGuild.id ? "bg-accent" : "hover:bg-card/60"
              }`}
            >
              {g.id === activeGuild.id ? <Check className="h-3 w-3 text-green-500" /> : <span className="w-3" />}
              <span className="flex-1 truncate font-mono">{g.name}</span>
              <span className="text-[9px] text-muted-foreground uppercase">{g.role}</span>
            </button>
          ))}
          <div className="border-t border-border/40 my-1" />

          {/* Create new */}
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <button data-testid="guild-create-trigger"
                className="w-full flex items-center gap-2 px-2 py-1.5 rounded-sm text-xs text-purple-400 hover:bg-purple-500/10 transition-colors">
                <Plus className="h-3 w-3" /> Create new guild
              </button>
            </DialogTrigger>
            <DialogContent className="max-w-sm bg-background">
              <DialogHeader>
                <DialogTitle className="heading text-base">Create a new guild</DialogTitle>
              </DialogHeader>
              <Input
                data-testid="guild-create-modal-name"
                placeholder="Guild name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                className="bg-card/50 font-mono"
              />
              <div className="flex justify-end gap-2">
                <DialogClose asChild>
                  <Button variant="outline" size="sm" className="text-xs rounded-sm">Cancel</Button>
                </DialogClose>
                <Button data-testid="guild-create-modal-btn" onClick={handleCreate} disabled={busy}
                  size="sm" className="text-xs rounded-sm bg-purple-500 hover:bg-purple-600 text-white">
                  {busy ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null} Create
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Invite (admins only) */}
          {isAdmin && (
            <Dialog open={inviteOpen} onOpenChange={(v) => { setInviteOpen(v); if (!v) setInviteCode(""); }}>
              <DialogTrigger asChild>
                <button data-testid="guild-invite-trigger"
                  className="w-full flex items-center gap-2 px-2 py-1.5 rounded-sm text-xs text-blue-400 hover:bg-blue-500/10 transition-colors">
                  <Plus className="h-3 w-3" /> Generate invite link
                </button>
              </DialogTrigger>
              <DialogContent className="max-w-sm bg-background">
                <DialogHeader>
                  <DialogTitle className="heading text-base">Invite to {activeGuild.name}</DialogTitle>
                </DialogHeader>
                {!inviteCode ? (
                  <>
                    <div>
                      <label className="text-xs text-muted-foreground uppercase mb-1 block font-mono">Role</label>
                      <Select value={inviteRole} onValueChange={setInviteRole}>
                        <SelectTrigger data-testid="invite-role-select" className="bg-card/50"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="admin">Admin</SelectItem>
                          <SelectItem value="officer">Officer</SelectItem>
                          <SelectItem value="visitor">Visitor (read-only)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button data-testid="generate-invite-btn" onClick={handleInvite} disabled={inviteBusy}
                      className="rounded-sm font-bold bg-blue-500 hover:bg-blue-600 text-white">
                      {inviteBusy ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null} Generate code
                    </Button>
                  </>
                ) : (
                  <>
                    <p className="text-xs text-muted-foreground">Send this code to whoever you want to invite. It's single-use.</p>
                    <div className="flex items-center gap-2">
                      <Input data-testid="invite-code-output" value={inviteCode} readOnly className="bg-card/50 font-mono text-xs" />
                      <Button data-testid="copy-invite-btn" size="sm" onClick={copyInvite} className="rounded-sm gap-1">
                        <Copy className="h-3 w-3" /> Copy
                      </Button>
                    </div>
                  </>
                )}
              </DialogContent>
            </Dialog>
          )}
        </div>
      )}
    </div>
  );
}
